import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { CampaignStatus, CampaignType, ICampaign } from "./Addressable.types";
import dayjs from "dayjs";
import { Chip } from "@mui/material";
import { ptBR } from "@mui/x-data-grid/locales";

type Props = {
  campaigns: ICampaign[];
  onCampaignSelect: (campaign: ICampaign | null) => void;
}

export function AddressableCampaignStatus({ campaigns, onCampaignSelect }: Props) {

  const formatDate = (date: Date | null) => {
    if (!date) return "-";
    return dayjs(date).format("DD/MM/YYYY HH:mm");
  };

  const formatStatus = (status: CampaignStatus) => {
    const statusConfig = {
      PENDING: {
        label: "Em andamento",
        backgroundColor: "rgba(2, 136, 209, 0.08)",
        color: "#01579B"
      },
      FINISHED: {
        label: "Concluída",
        backgroundColor: "rgba(46, 125, 50, 0.08)",
        color: "#2E7D32"
      },
    };

    const config = statusConfig[status];

    return (
      <Chip
        label={config.label}
        size="small"
        sx={{
          backgroundColor: config.backgroundColor,
          color: config.color,
          fontWeight: 500,
        }}
      />
    );
  };

  const formatType = (type: CampaignType) => {
    if (type === "MISSING_DATA") return "Dados faltantes";

    if (type === "DATA_GOVERNANCE") return "Governança de dados";
  }

  const columns: GridColDef<ICampaign>[] = [
    {
      field: "sentDate",
      headerName: "Iniciada em",
      flex: 1,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: "finishedDate",
      headerName: "Finalizada em",
      flex: 1,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: "type",
      headerName: "Campanha",
      flex: 1,
      renderCell: (params) => formatType(params.value),
    },
    {
      field: "metricTotal",
      headerName: "Total",
      flex: 1,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "metricPending",
      headerName: "Pendentes",
      flex: 1,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "metricDone",
      headerName: "Concluídas",
      flex: 1,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => formatStatus(params.value),
    },
  ];

  return (
    <>
      <DataGrid
        rows={campaigns}
        columns={columns}
        localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 5,
            },
          },
        }}
        pageSizeOptions={[5, 10, 20, 50]}
        style={{ backgroundColor: "white", minHeight: 400 }}
        disableColumnFilter={false}
        disableColumnSelector={false}
        disableDensitySelector={false}
        scrollbarSize={10}
        onRowClick={(params) => {
          const campaign = campaigns.find(c => c.id === params.id);
          if (campaign) onCampaignSelect(campaign);
        }}
        sx={{
          "& .MuiDataGrid-row": {
            cursor: "pointer",
          },
          "& .MuiDataGrid-row[data-rowselectable='false']": {
            "& .MuiCheckbox-root": {
              opacity: 0.3,
              cursor: "not-allowed",
            },
            "& .MuiCheckbox-root.Mui-disabled": {
              opacity: 0.3,
              cursor: "not-allowed",
            },
          },
          "& .MuiDataGrid-checkboxInput.Mui-disabled": {
            opacity: 0.3,
            cursor: "not-allowed",
          },
        }}
      />
    </>
  );
}
